/**
 * useClassifyItem — shared hook for calling the WADM GetItemInformation API,
 * managing loading state, and navigating to the results page.
 */

import { useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppSelector } from "src/store/store";
import { getItemInformation } from "src/services/classificationService";
import { ItemFlowContext } from "src/types/containerTypes";

interface ClassifyScannerLocationState {
  itemFlowContext?: ItemFlowContext;
}

export const useClassifyItem = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeSiteCode } = useAppSelector((state) => state.user);
  const itemFlowContext = (location.state as ClassifyScannerLocationState)?.itemFlowContext;

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scannedAsin, setScannedAsin] = useState<string>("");

  const classify = useCallback(
    async (asin: string) => {
      const value = asin.trim();
      if (!value) return;

      setError(null);
      setScannedAsin(value);
      setIsAnalyzing(true);

      try {
        const data = await getItemInformation(value, activeSiteCode);
        navigate("/classification/results", { state: { data, itemFlowContext } });
      } catch (err: any) {
        setError(err?.message || "Failed to fetch classification data");
        setIsAnalyzing(false);
        throw err;
      }
    },
    [activeSiteCode, navigate],
  );

  return { classify, isAnalyzing, error, scannedAsin };
};
